import React, { useMemo } from 'react';
import {
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Bar,
    BarChart,
    Cell,
    LabelList,
    ReferenceLine,
} from 'recharts';
import { motion } from 'framer-motion';
import { JURISDICTION_GROUPS } from '../../utils/jurisdictionData';
import { BarChart3, MapPin } from 'lucide-react';

/* County accent colours */
const COUNTY_META = {
    'Santa Clara': { hex: '#f472b6', soft: 'rgba(244,114,182,0.12)', ring: 'rgba(244,114,182,0.3)', text: '#9d174d', label: '#f9a8d4' },
    'Alameda': { hex: '#22d3ee', soft: 'rgba(34,211,238,0.12)', ring: 'rgba(34,211,238,0.3)', text: '#0e7490', label: '#67e8f9' },
    'San Mateo': { hex: '#818cf8', soft: 'rgba(129,140,248,0.12)', ring: 'rgba(129,140,248,0.3)', text: '#4338ca', label: '#a5b4fc' },
    'Contra Costa': { hex: '#fbbf24', soft: 'rgba(251,191,36,0.12)', ring: 'rgba(251,191,36,0.3)', text: '#92400e', label: '#fcd34d' },
    'Marin': { hex: '#10b981', soft: 'rgba(16,185,129,0.12)', ring: 'rgba(16,185,129,0.3)', text: '#065f46', label: '#6ee7b7' },
    'Napa': { hex: '#f59e0b', soft: 'rgba(245,158,11,0.12)', ring: 'rgba(245,158,11,0.3)', text: '#92400e', label: '#fcd34d' },
    'Solano': { hex: '#f43f5e', soft: 'rgba(244,63,94,0.12)', ring: 'rgba(244,63,94,0.3)', text: '#9f1239', label: '#fda4af' },
};

/* ─── Custom Tooltip ─── */
const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const meta = COUNTY_META[d.name] || {};
    const isGhost = d.isGhost;

    return (
        <div
            className="p-4 rounded-2xl shadow-xl"
            style={{
                background: '#ffffff',
                border: `1.5px solid ${meta.ring}`,
                boxShadow: `0 16px 40px rgba(0,0,0,0.12), 0 0 0 1px ${meta.ring}`,
                minWidth: 160,
            }}
        >
            <div className="flex items-center gap-2 mb-3">
                <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ background: meta.hex, boxShadow: `0 0 6px ${meta.hex}` }}
                />
                <span className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400">
                    {d.name} County
                </span>
            </div>
            <p className="text-3xl font-black text-slate-900 tabular-nums leading-none">
                {isGhost ? '—' : d.count}
            </p>
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-1">
                {isGhost ? 'No Activity Currently' : 'Properties Analyzed'}
            </p>
            {!isGhost && d.share > 0 && (
                <div
                    className="mt-3 pt-3 flex items-center justify-between"
                    style={{ borderTop: '1px solid #f1f5f9' }}
                >
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Portfolio share</span>
                    <span className="text-sm font-black tabular-nums" style={{ color: meta.text }}>
                        {d.share}%
                    </span>
                </div>
            )}
            {isGhost && (
                <div className="mt-3 pt-3 flex items-center gap-2" style={{ borderTop: '1px solid #f1f5f9' }}>
                    <div className="w-1 h-1 rounded-full bg-slate-300" />
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Available for Analysis</span>
                </div>
            )}
        </div>
    );
};

/* ─── Custom top label ─── */
const BarTopLabel = (props) => {
    const { x, y, width, value, fill, onSelectCounty, selectedCounty, name, isGhost } = props;
    if (value === undefined || value === null) return null;
    return (
        <text
            x={x + width / 2}
            y={y - 12}
            textAnchor="middle"
            fontSize={9}
            fontWeight={900}
            fill={fill || '#94a3b8'}
            style={{ cursor: 'pointer', opacity: isGhost ? 0.3 : 1 }}
            onClick={() => onSelectCounty && onSelectCounty(selectedCounty === name ? null : name)}
        >
            {isGhost ? '—' : value}
        </text>
    );
};

/* ═══════════════════════════════════════════════
 *  MAIN COMPONENT
 * ═══════════════════════════════════════════════ */
const RegionalLineChart = ({ analysisHistory, onSelectCounty, selectedCounty }) => {
    const chartData = useMemo(() => {
        const counts = JURISDICTION_GROUPS.reduce((acc, g) => {
            acc[g.county] = 0;
            return acc;
        }, {});

        if (analysisHistory?.length) {
            analysisHistory.forEach(item => {
                const addr = (item.address || '').toLowerCase();
                let matched = false;
                for (const group of JURISDICTION_GROUPS) {
                    if (group.cities.some(c => addr.includes(c.toLowerCase()))) {
                        counts[group.county]++;
                        matched = true;
                        break;
                    }
                }
                if (!matched) console.warn('⚠️ Unmatched address:', item.address);
            });
        }

        const total = Object.values(counts).reduce((s, n) => s + n, 0);
        const maxCount = Math.max(...Object.values(counts), 1);
        const ghostHeight = maxCount * 0.06; // 6% of max bar height for the ghost placeholder

        return JURISDICTION_GROUPS.map(group => {
            const count = counts[group.county];
            const isGhost = count === 0;
            return {
                name: group.county,
                count: count,
                displayCount: isGhost ? ghostHeight : count,
                isGhost: isGhost,
                share: total > 0 ? ((counts[group.county] / total) * 100).toFixed(1) : '0.0',
                color: COUNTY_META[group.county]?.hex || '#94a3b8',
            };
        });
    }, [analysisHistory]);

    const totalProperties = chartData.reduce((s, d) => s + d.count, 0);
    const avgLine = totalProperties / chartData.length;

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
            style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '2rem',
                boxShadow: '0 6px 32px rgba(0,0,0,0.07)',
            }}
        >

            {/* Tip */}
            <div
                className="px-4 py-2.5 flex items-center gap-2"
                style={{ background: '#187fe7ff', border: '1px solid #f1f5f9' }}
            >
                <MapPin className="w-3 h-3 text-slate-300 flex-shrink-0" />
                <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-white">
                    Click a county chip or summary card to drill into city-level insights
                </p>
            </div>

            <div className="p-5">

                {/* ── HEADER ── */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-5 mb-8">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <BarChart3 className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                                Market Activity
                            </span>
                        </div>
                        <h3 className="text-xl font-extrabold text-slate-900 leading-tight">
                            County Portfolio{' '}
                            <span className="text-slate-400 font-semibold">Breakdown</span>
                        </h3>
                        <p className="text-xs text-slate-400 font-medium mt-1">
                            Property distribution across all active territories
                        </p>
                    </div>

                    {/* County filter pills */}
                    <div className="flex flex-wrap items-center gap-2">
                        {JURISDICTION_GROUPS.map((group) => {
                            const meta = COUNTY_META[group.county] || {};
                            const isActive = selectedCounty === group.county;
                            const d = chartData.find(x => x.name === group.county);
                            return (
                                <motion.button
                                    key={group.county}
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => onSelectCounty(isActive ? null : group.county)}
                                    title={d?.count === 0 ? `No properties analyzed in ${group.county} yet` : ""}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black tracking-widest uppercase transition-all whitespace-nowrap"
                                    style={{
                                        background: isActive ? meta.hex : meta.soft || 'rgba(148,163,184,0.1)',
                                        color: isActive ? '#ffffff' : meta.text || '#64748b',
                                        border: `1.5px solid ${isActive ? meta.hex : meta.ring || 'rgba(148,163,184,0.2)'}`,
                                        boxShadow: isActive ? `0 4px 16px ${meta.ring}` : 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div
                                        className="w-1.5 h-1.5 rounded-full"
                                        style={{ background: isActive ? '#ffffff' : meta.hex || '#94a3b8' }}
                                    />
                                    {group.county}
                                    <span
                                        className="px-1.5 py-0.5 rounded-full text-[8px] font-black tabular-nums"
                                        style={{
                                            background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)',
                                            color: isActive ? '#ffffff' : meta.text || '#64748b',
                                        }}
                                    >
                                        {d?.count || 0}
                                    </span>
                                </motion.button>
                            );
                        })}
                    </div>
                </div>

                {/* ── CHART ── */}
                <div className="h-[360px] w-full relative">
                    {/* Selected County Empty State Overlay */}
                    {selectedCounty && chartData.find(d => d.name === selectedCounty)?.count === 0 && (
                        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[2px] rounded-2xl pointer-events-none">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center text-center p-6 bg-white/80 border border-slate-200/60 shadow-2xl rounded-3xl"
                            >
                                <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-300 mb-4 border border-slate-100">
                                    <MapPin className="w-6 h-6" />
                                </div>
                                <h4 className="text-lg font-black text-slate-900 mb-1">No Data for {selectedCounty}</h4>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed max-w-[200px]">
                                    Analyze a property in this region to populate your regional portfolio
                                </p>
                            </motion.div>
                        </div>
                    )}

                    {totalProperties === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="flex flex-col items-center text-center p-8"
                            >
                                <div className="relative mb-6">
                                    <div className="absolute inset-0 bg-blue-100 rounded-full blur-2xl opacity-40 animate-pulse"></div>
                                    <div className="relative bg-gradient-to-br from-slate-50 to-white p-6 rounded-full shadow-inner border border-slate-100">
                                        <BarChart3 className="w-12 h-12 text-slate-300" />
                                    </div>
                                </div>
                                <h4 className="text-xl font-black text-slate-900 mb-2">Portfolio Awaiting Data</h4>
                                <p className="text-sm text-slate-500 font-medium max-w-[280px] leading-relaxed">
                                    Your regional property distribution will materialize here once you begin your first property analysis.
                                </p>
                                <div className="mt-8 flex items-center gap-3">
                                    <div className="h-px w-8 bg-slate-200"></div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Ready for Insights</span>
                                    <div className="h-px w-8 bg-slate-200"></div>
                                </div>
                            </motion.div>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 32, right: 24, left: 0, bottom: 8 }}
                                barCategoryGap="30%"
                            >
                                {/* Subtle horizontal grid */}
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
                                    tickFormatter={v => v === 0 ? '' : v}
                                    width={28}
                                />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={({ payload, verticalAnchor, visibleTicksCount, tickFormatter, ...rest }) => (
                                        <text
                                            {...rest}
                                            fill="#64748b"
                                            fontSize={11}
                                            fontWeight={800}
                                            dy={14}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => onSelectCounty(selectedCounty === payload.value ? null : payload.value)}
                                        >
                                            {payload.value}
                                        </text>
                                    )}
                                />
                                {/* Average reference line */}
                                {totalProperties > 0 && (
                                    <ReferenceLine
                                        y={avgLine}
                                        stroke="#e2e8f0"
                                        strokeDasharray="6 4"
                                        strokeWidth={1.5}
                                        label={{
                                            value: `avg ${avgLine.toFixed(1)}`,
                                            position: 'insideTopRight',
                                            fontSize: 9,
                                            fontWeight: 700,
                                            fill: '#94a3b8',
                                            dy: -4,
                                        }}
                                    />
                                )}
                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={{ fill: '#f8fafc', radius: 12 }}
                                />
                                <Bar
                                    dataKey="displayCount"
                                    radius={[12, 12, 4, 4]}
                                    maxBarSize={90}
                                    animationDuration={1200}
                                    animationEasing="ease-out"
                                    style={{ cursor: 'pointer' }}
                                    onClick={(data) => {
                                        if (data && data.name) {
                                            onSelectCounty(selectedCounty === data.name ? null : data.name);
                                        }
                                    }}
                                >
                                    <LabelList
                                        dataKey="displayCount"
                                        content={(props) => {
                                            const entry = chartData[props.index];
                                            const countyName = entry?.name;
                                            const meta = COUNTY_META[countyName] || {};
                                            return (
                                                <BarTopLabel
                                                    {...props}
                                                    name={countyName}
                                                    value={entry?.count}
                                                    isGhost={entry?.isGhost}
                                                    fill={meta.text || '#334155'}
                                                    onSelectCounty={onSelectCounty}
                                                    selectedCounty={selectedCounty}
                                                />
                                            );
                                        }}
                                    />
                                    {chartData.map((entry, idx) => {
                                        const dimmed = selectedCounty && selectedCounty !== entry.name;
                                        return (
                                            <Cell
                                                key={`cell-${idx}`}
                                                fill={entry.color}
                                                fillOpacity={entry.isGhost ? 0.08 : (dimmed ? 0.15 : 1)}
                                                stroke={entry.isGhost ? entry.color : 'none'}
                                                strokeDasharray={entry.isGhost ? "6 4" : "0"}
                                                strokeWidth={entry.isGhost ? 1.5 : 0}
                                                style={{
                                                    filter: dimmed ? 'none' : (entry.isGhost ? 'none' : `drop-shadow(0 4px 12px ${entry.color}55)`),
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        );
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default RegionalLineChart;
